import { userOnboardingSchema } from "@brookbush/types";

import type { ComponentType } from "react";
import z from "zod";

import OnboardingWizardStepAuth from "./OnboardingWizardStepAuth";
import OnboardingWizardStepCertification from "./OnboardingWizardStepCertification";
import OnboardingWizardStepCeuGoals from "./OnboardingWizardStepCeuGoals";
import OnboardingWizardStepComplete from "./OnboardingWizardStepComplete";
import OnboardingWizardStepPhone from "./OnboardingWizardStepPhone";
import OnboardingWizardStepProfession from "./OnboardingWizardStepProfession";
import OnboardingWizardStepWelcome from "./OnboardingWizardStepWelcome";
import type { WizardMode } from "./WizardModeContext";
import type { LearningPathChoice, WizardStepKey } from "~/hooks/useOnboarding";

export const wizardFormSchema = userOnboardingSchema.extend({
	learningPath: z.enum(["certification", "ceu"]).nullable(),
	primaryProfessionId: z.string().min(1, "Select a primary profession"),
	interests: z.array(z.string()).max(5),
	ceuGoals: z.record(
		z.string(),
		z.object({
			selected: z.boolean().optional(),
			targetCredits: z.number().optional(),
			renewalDate: z.string().optional(),
		}),
	),
});

export type WizardFormValues = z.infer<typeof wizardFormSchema>;

export const STEPS: Record<
	WizardStepKey,
	{
		title: string;
		index: number;
		prev: (
			learningPath: LearningPathChoice | null,
			mode: WizardMode,
		) => WizardStepKey | null;
		Component: ComponentType;
	}
> = {
	welcome: {
		title: "Welcome",
		index: 0,
		prev: () => null,
		Component: OnboardingWizardStepWelcome,
	},
	profession: {
		title: "Your Profession",
		index: 1,
		prev: () => "welcome",
		Component: OnboardingWizardStepProfession,
	},
	certification: {
		title: "Choose Certification",
		index: 2,
		prev: () => "profession",
		Component: OnboardingWizardStepCertification,
	},
	"ceu-goals": {
		title: "Renewal Goals",
		index: 2,
		prev: () => "certification",
		Component: OnboardingWizardStepCeuGoals,
	},
	phone: {
		title: "Phone Number",
		index: 3,
		prev: (lp) => (lp === "certification" ? "certification" : "ceu-goals"),
		Component: OnboardingWizardStepPhone,
	},
	auth: {
		title: "Create Account",
		index: 3,
		prev: (lp, mode) =>
			mode === "deferred"
				? lp === "certification"
					? "certification"
					: "ceu-goals"
				: "phone",
		Component: OnboardingWizardStepAuth,
	},
	complete: {
		title: "All Done!",
		index: 4,
		prev: () => null,
		Component: OnboardingWizardStepComplete,
	},
};
